import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, resolveTenantSlug } from '../lib/api';
import type { TenantBranding } from '../lib/types';

interface TenantCtx {
  branding: TenantBranding | null;
  loading: boolean;
  error: string | null;
  /** Is an optional module enabled for this tenant? Core modules (not in the list) are always true. */
  moduleEnabled: (key: string) => boolean;
}
const Ctx = createContext<TenantCtx | null>(null);

// Modules that can be toggled off. Anything NOT here is core and always shown.
/*
 * ⚠ THE HAND-TYPED LIST OF TOGGLEABLE MODULES IS GONE, AND FINDING OUT WHY IS WORTH THIRTY SECONDS.
 *
 * There used to be a Set here naming the toggleable modules. It listed twelve. The backend enum had
 * thirteen — `News` was missing — so a SuperAdmin who switched News off got a 403 from the API and
 * the nav entry stayed on screen, which is the exact "shows people buttons that 403" failure the
 * FeatureGuard comment warns about. Nobody had noticed, because nobody switches News off.
 *
 * Three hand-maintained lists (the backend enum, ROUTE_MODULE in AppShell, and this one) cannot be
 * kept in step by discipline; adding five more modules would have made it four drifts waiting to
 * happen. So the client no longer holds an opinion about WHICH modules are toggleable — the server
 * says so. A route with no ROUTE_MODULE entry is core and always visible; a route with one is gated
 * on whatever the branding payload reports as enabled.
 */

/**
 * ⚠ A COLOUR IS ONLY APPLIED IF IT IS A COLOUR.
 *
 * `setProperty('--accent', undefined)` does not skip the write — it writes the literal string
 * "undefined" as an INLINE custom property on <html>, which outranks the good default in :root. Every
 * `background: var(--accent)` in the product then computes to transparent, so `.btn-accent` becomes
 * near-black text on no background: readable by luck on the light theme, invisible on the dark one.
 * The stylesheet's fallback cannot save it, because the bad value wins the cascade before the
 * fallback is ever consulted.
 *
 * The domain today defaults both colours to a real hex, so this cannot fire from our own API. It is
 * guarded anyway because the cost is four lines and the failure is silent, theme-dependent, and
 * product-wide — and because `darken(undefined)` would throw inside the .then, which the .catch
 * below would report to the person as "Could not load this institution."
 */
const isHex = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim());

// derive a readable "ink" version of the accent for text on light backgrounds
function darken(hex: string, amount = 0.4): string {
  const m = hex.replace('#', '');
  if (m.length !== 6) return hex;
  const r = Math.round(parseInt(m.slice(0, 2), 16) * (1 - amount));
  const g = Math.round(parseInt(m.slice(2, 4), 16) * (1 - amount));
  const b = Math.round(parseInt(m.slice(4, 6), 16) * (1 - amount));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const slug = resolveTenantSlug();
    if (!slug) {
      setError('No institution selected. Add ?tenant=your-slug to the URL for local access.');
      setLoading(false);
      return;
    }
    api.get<TenantBranding>('/api/tenant/branding')
      .then((b) => {
        setBranding(b);
        const root = document.documentElement.style;
        if (isHex(b.primaryColor)) root.setProperty('--brand', b.primaryColor.trim());
        if (isHex(b.accentColor)) {
          root.setProperty('--accent', b.accentColor.trim());
          root.setProperty('--accent-ink', darken(b.accentColor.trim()));
        }
        if (b.logoUrl) root.setProperty('--tenant-logo', `url("${b.logoUrl}")`);
        document.title = `${b.name} · Eduvora`;
      })
      .catch((e) => setError(e.message ?? 'Could not load this institution.'))
      .finally(() => setLoading(false));
  }, []);

  const moduleEnabled = (key: string): boolean => {
    if (!key) return true;                          // core route — no module, always on
    if (!branding) return true;                     // before load, don't hide anything
    // ⚠ `?? true` matters: an older API build that does not send enabledModules at all must show
    // everything, not hide the whole product. Absent means unknown, not disabled.
    return branding.enabledModules?.includes(key) ?? true;
  };

  return <Ctx.Provider value={{ branding, loading, error, moduleEnabled }}>{children}</Ctx.Provider>;
}

export function useTenant() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTenant must be used within TenantProvider');
  return c;
}
